'use client';

import { gql } from '@/__generated__';
import BlockHeader from '@/components/BlockHeader';
import { EventCard } from '@/components/Cards/EventCard';
import Carousel from '@/components/Carousel';
import CtaButton from '@/components/CtaButton';
import SkeletonCards from '@/components/SkeletonCards';
import Txt from '@/components/ui/Txt';
import {
  ACCESSIBILITY_CATEGORY_FRAGMENT,
  EVENT_TYPE_CATEGORY_FRAGMENT,
  GENRE_CATEGORY_FRAGMENT,
  PROGRAM_CATEGORY_FRAGMENT,
  VENUE_CATEGORY_FRAGMENT,
} from '@/gql/fragments/categories.gql';
import { EVENT_CARD_FRAGMENT } from '@/gql/fragments/entryCards.gql';
import useCommonLinks from '@/lib/getters/useCommonLinks';
import { useEventIndexEntries } from '@/lib/getters/useEventIndexEntries';
import { useEventTimeEntries } from '@/lib/getters/useEventTimeEntries';
import { createBlock } from '@/lib/parsers/blocks';
import { parseDateRange } from '@/lib/parsers/common';
import { useAppContext } from '@/lib/store/hooks';
import { fmtDateVar } from '@/lib/utils/fmtDateVar';
import { simplifyCategory } from '@/lib/utils/format';
import { useDayjs } from '@/lib/utils/useDayjs';
import { useBreakpointMin } from '@/theme';
import { createFragmentArrayParser } from '@liquorice/gql-utils';
import { excludeEmptyTypedElements, toBoolean } from '@liquorice/utils';
import { useTranslations } from 'next-intl';
import { BlockContainer } from '../..';

export const EVENT_INDEX_BLOCK_FRAGMENT = gql(`
  fragment eventIndex on blocks_eventIndex_BlockType {
    heading

    # Filters
    dateRange {
      end
      isFuture
      isNotPast
      isOnGoing
      isPast
      start
    }
    venueCategory {
      ...categories
    }
    programCategory {
      ...categories
    }
    accessibilityCategory {
      ...categories
    }
    genreCategory {
      ...categories
    }
    eventTypeCategory {
      ...categories
    }
    educationEvent
    includeEventIndex: lightswitch
    overrideEvents: eventMultiple {
      ...eventCard
    }
  }
`);

// TODO: clean up this file to make it more readable and maintainable

export const EventIndexBlock = createBlock(EVENT_INDEX_BLOCK_FRAGMENT, ({ data, meta }) => {
  const {
    venueCategory: venue,
    programCategory: program,
    accessibilityCategory: accessibility,
    eventTypeCategory: eventType,
    genreCategory: genre,
    dateRange: range,
    heading,
    includeEventIndex,
  } = data;

  const t = useTranslations();

  // Parse categories
  const genreCategory = simplifyCategory(GENRE_CATEGORY_FRAGMENT, genre);
  const venueCategory = simplifyCategory(VENUE_CATEGORY_FRAGMENT, venue);
  const programCategory = simplifyCategory(PROGRAM_CATEGORY_FRAGMENT, program);
  const accessibilityCategory = simplifyCategory(ACCESSIBILITY_CATEGORY_FRAGMENT, accessibility);
  const eventTypeCategory = simplifyCategory(EVENT_TYPE_CATEGORY_FRAGMENT, eventType);

  // Check if education event is specified
  const isEducationEvent = toBoolean(data.educationEvent);

  // Parse date range
  const dateRange = parseDateRange(range);

  // Get the event index URI and include link if specified
  const { eventIndex } = useCommonLinks();
  const eventIndexUri = eventIndex.uri;
  const includeLink = toBoolean(includeEventIndex);

  // Prep date variables for the event time query
  const { end, start } = dateRange ?? {};
  const currentDate = useDayjs().toISOString();

  // Determine the date variable for the event time query
  const whichDate = end || start ? { start, end } : { start: useDayjs(currentDate).toISOString() };

  const date = fmtDateVar(whichDate);

  const { result, loading: eventTimeLoading } = useEventTimeEntries({ date });

  const eventEntryIds = result.map((v) => v.eventEntryId).filter(Boolean) ?? [];

  const { sortedItems, loading: eventLoading } = useEventIndexEntries({
    genreCategory: genreCategory?.map((v) => v.id),
    venueCategory: venueCategory?.map((v) => v.id),
    programCategory: programCategory?.map((v) => v.id),
    accessibilityCategory: accessibilityCategory?.map((v) => v.id),
    eventTypeCategory: eventTypeCategory?.map((v) => v.id),
    educationEvent: isEducationEvent,
    id: eventEntryIds,
  });

  const parseEventCard = createFragmentArrayParser(EVENT_CARD_FRAGMENT, (data) => data);

  const overrideEvents = excludeEmptyTypedElements(data.overrideEvents);
  const overrideEventsParsed = parseEventCard(overrideEvents);
  const hasOverrideEvents = overrideEventsParsed && overrideEventsParsed.length > 0;

  const hasResults = (sortedItems?.length && (sortedItems?.length ?? 0) > 0) || hasOverrideEvents;

  const md = useBreakpointMin('md');

  const { data: page } = useAppContext() ?? {};
  const isEventPage = page?.__typename === 'event_Entry';

  // if (error) return null;

  return (
    <BlockContainer
      {...{
        colorSet: 'white',
        paper: true,
        meta,
        marginY: true,
        overflow: true,
        customAnchor: heading,
      }}>
      <BlockHeader
        maxWidth="none"
        heading={heading}
        HeadingProps={{ variant: 'xl', cx: { m: 'none' }, autoMargin: false, uppercase: true }}
        cx={{ mB: '2xl' }}
        direction="row"
        alignItems="end"
        columnGap={{ md: '4xl' }}
        justifyContent="between">
        {eventIndexUri && includeLink && (
          <CtaButton href={eventIndexUri} underline="always" color="black">
            {t('actions.viewMore')}
          </CtaButton>
        )}
      </BlockHeader>
      {eventTimeLoading || eventLoading ? (
        <SkeletonCards
          amount={md ? 3 : 1}
          cx={{ flexWrap: { md: 'nowrap' } }}
          md={6}
          lg={4}
          SkeletonItems={{
            items: [
              { variant: 'rounded', height: 200 },
              { variant: 'rounded', height: 30, width: '90%' },
              { variant: 'rounded', height: 15, width: '50%' },
            ],
          }}
        />
      ) : hasResults ? (
        <Carousel
          slidesPerView={md ? 3 : 1}
          slidesPerGroup={md ? 3 : 1}
          spaceBetween={24}
          style={{ overflow: isEventPage ? 'hidden' : 'visible' }}
          disablePagination={!isEventPage}
          disableNavigation={isEventPage}
          items={(hasOverrideEvents ? overrideEventsParsed : sortedItems)?.map((v, i) => (
            <EventCard key={i} data={v} />
          ))}
        />
      ) : (
        <Txt>{t('search.noResults', { query: heading })}</Txt>
      )}
    </BlockContainer>
  );
});

export default EventIndexBlock;
